// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the phone long-press menu for a message, in Konvo chats and WhatsApp alike (JP 2026-11-03,
// chats-lens). Built the way WhatsApp on iOS does it: the pressed message lifts out of the dimmed
// thread, its quick reactions ride above it and its actions sit under it, in the glass menu's material
// (GlassMenu: a frosted panel holding cards, the main actions as buttons on top). The message is a
// clone of the one in the thread, so it keeps every detail (media, quotes, ticks) without rendering
// the message again. A long message is cut off so the menu still fits; a long menu scrolls.

import {useBottomSheetBackHandler} from '@app/features/app/hooks/useBottomSheetBackHandler';
import {GlassMenuItems, screenArea} from '@app/features/ui/glass_menu/GlassMenu';
import glass from '@app/features/ui/glass_menu/GlassMenu.module.css';
import {EDGE, type MessagePlacement, placeMessageMenu} from '@app/features/ui/glass_menu/GlassMenuPlacement';
import styles from '@app/features/ui/glass_menu/GlassMessageMenu.module.css';
import {PlusIcon} from '@app/features/ui/icons/Icons';
import type {MenuGroupType, MenuSubmenuItemType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import {resolvePortalHost, usePortalHost} from '@app/features/ui/overlay/PortalHostContext';
import LayerManager from '@app/features/ui/state/LayerManager';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import type React from 'react';
import {type ReactNode, useEffect, useId, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

const MESSAGE_ACTIONS_DESCRIPTOR = msg({
	message: 'Message actions',
	comment: 'Phone message menu (long press on a message): accessible name of the whole menu.',
});
const CLOSE_DESCRIPTOR = msg({
	message: 'Close',
	comment: 'Phone message menu: accessible name of the dimmed page behind it, which closes the menu.',
});

const WIDTH = 252;

export interface GlassMessageMenuProps {
	/** The message in the thread; it is cloned, never moved. */
	bubble: HTMLElement;
	/** The side the reactions and the menu line up with: yours on the right ('end'). */
	side: 'start' | 'end';
	/** Above the message: its quick reactions (GlassReactionBar). */
	above?: ReactNode;
	groups: ReadonlyArray<MenuGroupType>;
	/** Items with these ids leave their group for the button row on top of the menu. */
	quickIds?: ReadonlyArray<string>;
	/** The menu's width (252 by default); a wider one fits five quick buttons. */
	width?: number;
	onClose: () => void;
	'data-flx'?: string;
}

/** Copies the message, with the variables its thread sets on it, so it looks the same outside the thread. */
function cloneMessage(bubble: HTMLElement): HTMLElement {
	const clone = bubble.cloneNode(true) as HTMLElement;
	clone.style.transform = '';
	clone.style.width = '100%';
	clone.style.maxWidth = 'none';
	clone.style.margin = '0';
	clone.setAttribute('inert', '');
	for (const node of [clone, ...Array.from(clone.querySelectorAll<HTMLElement>('[data-flx], [id]'))]) {
		node.removeAttribute('data-flx');
		node.removeAttribute('id');
	}
	for (const node of clone.querySelectorAll('button[aria-haspopup="menu"]')) node.remove();
	const computed = window.getComputedStyle(bubble);
	for (const name of Array.from(computed)) {
		if (name.startsWith('--')) clone.style.setProperty(name, computed.getPropertyValue(name));
	}
	for (const name of ['background', 'color', 'border-radius', 'box-shadow', 'font', 'line-height']) {
		clone.style.setProperty(name, computed.getPropertyValue(name));
	}
	return clone;
}

export function GlassMessageMenu({
	bubble,
	side,
	above,
	groups,
	quickIds,
	width = WIDTH,
	onClose,
	'data-flx': flx = 'ui.glass-message-menu',
}: GlassMessageMenuProps) {
	const {i18n} = useLingui();
	const portalHost = usePortalHost();
	const cloneHostRef = useRef<HTMLDivElement | null>(null);
	const aboveRef = useRef<HTMLDivElement | null>(null);
	const menuRef = useRef<HTMLDivElement | null>(null);
	const backdropPressed = useRef(false);
	const [layout, setLayout] = useState<MessagePlacement | null>(null);
	const [trail, setTrail] = useState<Array<MenuSubmenuItemType>>([]);
	// Where the message was when it was pressed; the thread under the dim does not move.
	const anchor = useMemo(() => bubble.getBoundingClientRect(), [bubble]);

	useLayoutEffect(() => {
		const host = cloneHostRef.current;
		if (!host) return;
		host.replaceChildren(cloneMessage(bubble));
		return () => host.replaceChildren();
	}, [bubble]);

	// The whole stack (reactions, message, menu) is placed as a unit. Measured after every render, not
	// only on opening: a submenu changes the menu's height, and a Konvo message's reaction row (and with
	// it the list) only arrives after the first render.
	const measure = () => {
		const next = placeMessageMenu(
			anchor,
			aboveRef.current?.offsetHeight ?? 0,
			menuRef.current?.scrollHeight ?? 0,
			screenArea(),
		);
		setLayout((previous) =>
			previous &&
			previous.top === next.top &&
			previous.bubbleMax === next.bubbleMax &&
			previous.menuMax === next.menuMax
				? previous
				: next,
		);
	};
	useLayoutEffect(measure);
	const measureRef = useRef(measure);
	measureRef.current = measure;
	useEffect(() => {
		const onResize = () => measureRef.current();
		window.visualViewport?.addEventListener('resize', onResize);
		window.addEventListener('resize', onResize);
		return () => {
			window.visualViewport?.removeEventListener('resize', onResize);
			window.removeEventListener('resize', onResize);
		};
	}, []);

	// Escape (and Android's back) close the menu only: it is the top layer.
	const layerKey = useId();
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;
	useEffect(() => {
		LayerManager.addLayer('contextmenu', layerKey, () => onCloseRef.current());
		return () => LayerManager.removeLayer('contextmenu', layerKey);
	}, [layerKey]);
	useBottomSheetBackHandler(true, onClose);

	const sideStyle: React.CSSProperties =
		side === 'end'
			? {marginRight: Math.max(EDGE, window.innerWidth - anchor.right)}
			: {marginLeft: Math.max(EDGE, anchor.left)};
	const clipped = layout != null && anchor.height > layout.bubbleMax + 0.5;

	return createPortal(
		<div
			className={clsx(glass.auto, styles.layer)}
			role="dialog"
			aria-modal="true"
			aria-label={i18n._(MESSAGE_ACTIONS_DESCRIPTOR)}
		>
			<button
				type="button"
				className={clsx(glass.dim, glass.dimLift, styles.backdrop)}
				aria-label={i18n._(CLOSE_DESCRIPTOR)}
				onPointerDown={() => {
					backdropPressed.current = true;
				}}
				onClick={(event) => {
					// Lifting the finger that opened the menu lands here when the message moved out from
					// under it; only a tap that also began on the backdrop (or a key press) closes it.
					if (event.detail !== 0 && !backdropPressed.current) return;
					onClose();
				}}
				data-flx={`${flx}.backdrop`}
			/>
			<div
				className={clsx(styles.stack, side === 'end' ? styles.end : styles.start)}
				style={{top: layout?.top ?? anchor.top, visibility: layout ? 'visible' : 'hidden'}}
				data-flx={flx}
			>
				{above && (
					<div ref={aboveRef} className={styles.side} style={sideStyle}>
						{above}
					</div>
				)}
				<div
					className={clsx(styles.bubbleWrap, clipped && styles.clipped)}
					style={{marginLeft: anchor.left, width: anchor.width, maxHeight: layout?.bubbleMax}}
					aria-hidden="true"
				>
					<div className={styles.clone} ref={cloneHostRef} />
				</div>
				<div
					ref={menuRef}
					role="menu"
					aria-label={i18n._(MESSAGE_ACTIONS_DESCRIPTOR)}
					className={clsx(styles.side, styles.panel)}
					style={{...sideStyle, width: `min(${width}px, calc(100vw - ${EDGE * 2}px))`, maxHeight: layout?.menuMax}}
					data-flx={`${flx}.menu`}
				>
					<GlassMenuItems
						groups={groups}
						quickRows={quickIds ? [quickIds] : []}
						trail={trail}
						onTrail={setTrail}
						onClose={onClose}
						flx={flx}
					/>
				</div>
			</div>
		</div>,
		resolvePortalHost(portalHost),
	);
}

interface GlassReactionBarProps {
	/** The bar's accessible name ("React"). */
	label: string;
	/** The + at the end, which opens the emoji picker. */
	moreLabel: string;
	onMore: () => void;
	children: ReactNode;
	'data-flx'?: string;
	moreFlx?: string;
}

/** The quick reactions over a lifted message: a glass pill of emojis and a + for the picker. */
export function GlassReactionBar({
	label,
	moreLabel,
	onMore,
	children,
	'data-flx': flx,
	moreFlx,
}: GlassReactionBarProps) {
	return (
		<div className={styles.reactions} role="group" aria-label={label} data-flx={flx}>
			<div className={styles.reactionScroller}>{children}</div>
			<button type="button" className={styles.more} aria-label={moreLabel} onClick={onMore} data-flx={moreFlx}>
				<PlusIcon size={18} weight="bold" />
			</button>
		</div>
	);
}

interface GlassReactionProps {
	label: string;
	/** Already yours: tapping it again takes it off. */
	active?: boolean;
	onClick: () => void;
	children: ReactNode;
	'data-flx'?: string;
}

export function GlassReaction({label, active, onClick, children, 'data-flx': flx}: GlassReactionProps) {
	return (
		<button
			type="button"
			className={clsx(styles.reaction, active && styles.reactionActive)}
			aria-label={label}
			aria-pressed={active === undefined ? undefined : active}
			onClick={onClick}
			data-flx={flx}
		>
			{children}
		</button>
	);
}
